import * as React from "react";
import clsx from "clsx";
import { X } from "lucide-react";
import * as formInput from "../../../ui/components/Form/FormInput.style.css";
import type { User, UserOrganization } from "../../lib/api/users";
import * as userApi from "../../lib/api/users";
import type { OrganizationWithMembership } from "../../lib/api/users";
import { useAuth } from "../../lib/auth/hooks";
import { useDebouncedValue } from "../../../ui/components/Table/hooks/useDebouncedValue";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { OrganizationCard } from "./OrganizationCard";
import type { ContextButtonAction as ResourceCardMenuAction } from "../../../ui/components/ListView/components/ContextButton";
import * as confirmStyles from "./OrganizationDetailPage.css";
import { clearInviteToken } from "../../lib/auth/invite-pending";
import { useClaimPendingOrganizationInvite } from "../../lib/auth/useClaimPendingOrganizationInvite";
import { useI18n } from "../../lib/i18n";
import * as styles from "./JoinOrganizationSearch.css";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

export interface JoinOrganizationSearchProps {
  user: User | null | undefined;
  /** When false, clears local search state (e.g. modal closed). */
  active?: boolean;
  onMembershipChange?: () => void;
}

export function JoinOrganizationSearch({
  user,
  active = true,
  onMembershipChange,
}: JoinOrganizationSearchProps) {
  const { t } = useI18n();
  const { accessToken, refreshUser } = useAuth();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<OrganizationWithMembership[]>([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionOrgId, setActionOrgId] = React.useState<string | null>(null);
  const [leaveOrgTarget, setLeaveOrgTarget] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [leaveOrgLoading, setLeaveOrgLoading] = React.useState(false);
  const [leaveOrgError, setLeaveOrgError] = React.useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const [claimVersion, setClaimVersion] = React.useState(0);
  useClaimPendingOrganizationInvite({
    enabled: active,
    onClaimed: () => setClaimVersion((v) => v + 1),
  });

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const pendingOrganizations = user?.pendingOrganizations ?? [];

  const membershipIds = React.useMemo(
    () => new Set((user?.organizations ?? []).map((o) => o.id)),
    [user?.organizations],
  );

  const pendingOrgIds = React.useMemo(
    () => new Set(pendingOrganizations.map((o) => o.id)),
    [pendingOrganizations],
  );

  React.useEffect(() => {
    if (active) return;
    setSearchQuery("");
    setSearchResults([]);
    setSearchError(null);
    setSearchLoading(false);
    setActionError(null);
    setActionOrgId(null);
  }, [active]);

  React.useEffect(() => {
    if (!active || !token) {
      setSearchResults([]);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }

    const q = debouncedQuery.trim();
    let cancelled = false;
    setSearchLoading(true);
    setSearchError(null);

    userApi
      .searchOrganizations(token, q, 20)
      .then((orgs) => {
        if (cancelled) return;
        const sorted = [...orgs].sort((a, b) => {
          const score = (o: OrganizationWithMembership) => {
            if (o.isPreApproved && !o.isMember && !o.isPendingRequest) return 0;
            if (o.isPendingRequest) return 1;
            if (o.isMember) return 2;
            return 3;
          };
          return score(a) - score(b);
        });
        setSearchResults(sorted);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSearchResults([]);
          setSearchError(
            err instanceof Error ? err.message : "Failed to search organizations",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [active, token, debouncedQuery, claimVersion]);

  const refreshAfterMembershipAction = React.useCallback(async () => {
    await refreshUser();
    onMembershipChange?.();
  }, [refreshUser, onMembershipChange]);

  const handleRequestMembership = React.useCallback(
    async (org: OrganizationWithMembership) => {
      if (!token) return;
      setActionError(null);
      setActionOrgId(org.id);
      try {
        await userApi.requestOrganizationMembership(token, org.id);
        setSearchResults((prev) =>
          prev.map((row) =>
            row.id === org.id ? { ...row, isPendingRequest: true, isMember: false } : row,
          ),
        );
        await refreshAfterMembershipAction();
      } catch (err: unknown) {
        setActionError(
          err instanceof Error ? err.message : "Failed to request membership",
        );
      } finally {
        setActionOrgId(null);
      }
    },
    [token, refreshAfterMembershipAction],
  );

  const handleInstantJoin = React.useCallback(
    async (org: OrganizationWithMembership) => {
      if (!token) return;
      setActionError(null);
      setActionOrgId(org.id);
      try {
        await userApi.instantJoinOrganization(token, org.id);
        setSearchResults((prev) =>
          prev.map((row) =>
            row.id === org.id
              ? { ...row, isMember: true, isPendingRequest: false, isPreApproved: false }
              : row,
          ),
        );
        await refreshAfterMembershipAction();
        clearInviteToken();
      } catch (err: unknown) {
        setActionError(
          err instanceof Error ? err.message : "Failed to join organization",
        );
      } finally {
        setActionOrgId(null);
      }
    },
    [token, refreshAfterMembershipAction],
  );

  const handleCancelMembership = React.useCallback(
    async (orgId: string) => {
      if (!token) return;
      setActionError(null);
      setActionOrgId(orgId);
      try {
        await userApi.cancelOrganizationMembershipRequest(token, orgId);
        setSearchResults((prev) =>
          prev.map((row) =>
            row.id === orgId ? { ...row, isPendingRequest: false } : row,
          ),
        );
        await refreshAfterMembershipAction();
      } catch (err: unknown) {
        setActionError(
          err instanceof Error ? err.message : "Failed to cancel membership request",
        );
      } finally {
        setActionOrgId(null);
      }
    },
    [token, refreshAfterMembershipAction],
  );

  const handleConfirmLeaveOrganization = React.useCallback(async () => {
    if (!token || !leaveOrgTarget || !user?.id) return;
    setLeaveOrgLoading(true);
    setLeaveOrgError(null);
    try {
      await userApi.leaveOrganization(token, leaveOrgTarget.id, user.id);
      setSearchResults((prev) => prev.filter((row) => row.id !== leaveOrgTarget.id));
      setLeaveOrgTarget(null);
      await refreshAfterMembershipAction();
    } catch (err: unknown) {
      setLeaveOrgError(
        err instanceof Error ? err.message : "Failed to leave organization",
      );
    } finally {
      setLeaveOrgLoading(false);
    }
  }, [token, leaveOrgTarget, user?.id, refreshAfterMembershipAction]);

  const trimmedQuery = searchQuery.trim();
  const showSearchHint =
    trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH;

  const renderCard = (
    org: UserOrganization | OrganizationWithMembership,
    options: {
      isMember?: boolean;
      isPendingRequest?: boolean;
      isPreApproved?: boolean;
      onRequestMembership?: () => void;
      onInstantJoin?: () => void;
      onCancelMembershipRequest?: () => void;
      menuActions?: ResourceCardMenuAction[];
    },
  ) => (
    <OrganizationCard
      id={org.id}
      name={org.name}
      description={org.description}
      logoUrl={org.logoUrl}
      city={org.city}
      state={org.state}
      roles={
        "roleName" in org && org.roleName
          ? [{ name: org.roleName, color: org.roleColor }]
          : undefined
      }
      isMember={options.isMember}
      isPendingRequest={options.isPendingRequest}
      isPreApproved={options.isPreApproved}
      onRequestMembership={options.onRequestMembership}
      onInstantJoin={options.onInstantJoin}
      onCancelMembershipRequest={options.onCancelMembershipRequest}
      menuActions={options.menuActions}
      menuDisabled={leaveOrgLoading}
    />
  );

  return (
    <div className={styles.root}>
      <div className={formInput.wrapper}>
        <div className={formInput.passwordFieldWrapper}>
          <input
            type="text"
            className={clsx(
              formInput.input,
              searchQuery.length > 0 && formInput.passwordInput,
            )}
            placeholder={t("pro.orgs.searchPlaceholder")}
            aria-label={t("pro.orgs.searchAriaLabel")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              className={formInput.passwordToggle}
              onClick={() => setSearchQuery("")}
              tabIndex={-1}
              aria-label={t("pro.orgs.clearSearch")}
            >
              <X size={16} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <p className={styles.searchStatus} role="alert">
          {actionError}
        </p>
      )}

      {showSearchHint && (
        <p className={styles.searchStatus}>
          Enter at least {MIN_SEARCH_LENGTH} characters to search.
        </p>
      )}

      {searchError && (
        <p className={styles.searchStatus} role="alert">
          {searchError}
        </p>
      )}

      {searchLoading && (
        <p className={styles.searchStatus}>{t("pro.orgs.loadingOrganizations")}</p>
      )}

      {!searchLoading && !searchError && searchResults.length === 0 && (
        <p className={styles.searchStatus}>
          {debouncedQuery.trim().length >= MIN_SEARCH_LENGTH
            ? "No organizations found."
            : "Search to discover organizations, or add a pattern-approved org will appear here."}
        </p>
      )}

      {searchResults.length > 0 && (
        <div className={styles.searchResultsList} role="list">
          {searchResults.map((org) => {
            const isMember = org.isMember ?? membershipIds.has(org.id);
            const isPendingRequest =
              org.isPendingRequest ?? pendingOrgIds.has(org.id);
            const isPreApproved = org.isPreApproved === true;
            return (
              <div key={org.id} role="listitem">
                {renderCard(org, {
                  isMember,
                  isPendingRequest,
                  isPreApproved,
                  menuActions: isMember
                    ? [
                        {
                          id: "leave",
                          label: "Leave organization",
                          destructive: true,
                          onSelect: () =>
                            setLeaveOrgTarget({ id: org.id, name: org.name }),
                        },
                      ]
                    : undefined,
                  onInstantJoin:
                    isPreApproved && !isMember && !isPendingRequest && actionOrgId !== org.id
                      ? () => handleInstantJoin(org)
                      : undefined,
                  onRequestMembership:
                    !isMember &&
                    !isPendingRequest &&
                    !isPreApproved &&
                    actionOrgId !== org.id
                      ? () => handleRequestMembership(org)
                      : undefined,
                  onCancelMembershipRequest: isPendingRequest
                    ? actionOrgId === org.id
                      ? undefined
                      : () => handleCancelMembership(org.id)
                    : undefined,
                })}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={leaveOrgTarget !== null}
        setOpen={(open) => {
          if (!open && !leaveOrgLoading) {
            setLeaveOrgTarget(null);
            setLeaveOrgError(null);
          }
        }}
        minWidth="420px"
        minHeight="auto"
        maxWidth="480px"
      >
        {leaveOrgTarget && (
          <div className={confirmStyles.confirmModalBody}>
            <h3 className={confirmStyles.confirmModalTitle}>
              Leave organization?
            </h3>
            <p className={confirmStyles.confirmModalText}>
              If you confirm, you will no longer be a member of{" "}
              {leaveOrgTarget.name}.
            </p>
            {leaveOrgError && <Callout variant="error">{leaveOrgError}</Callout>}
            <div className={confirmStyles.confirmModalActions}>
              <Button
                color="neutral"
                variant="outline"
                size="md"
                disabled={leaveOrgLoading}
                onClick={() => {
                  setLeaveOrgTarget(null);
                  setLeaveOrgError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                variant="solid"
                size="md"
                loading={leaveOrgLoading}
                onClick={() => void handleConfirmLeaveOrganization()}
              >
                Leave organization
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
